"use client"
import { type CSSProperties } from "react"
import { motion } from "motion/react"
import { POLICY_ICONS, type Policy } from "./policiesData"
import { usePolicyHoverSwap } from "./policyHoverSwap"
import { usePolicyLayoutAnimationDisabled } from "./policyLayoutAnimationContext"
import { useHoverSwapsDisabled } from "../hoverSwapsDisabled"
import {
  POLICY_SQUARE_HALO_STYLE,
  POLICY_SQUARE_INSET_RING_STYLE,
  POLICY_SQUARE_POP_STYLE,
  POLICY_SQUARE_RED_BAR_STYLE,
  POLICY_SQUARE_RED_FILL_INVERT_STYLE,
} from "./policies-panel/policyAppearance"

const BOX_SIZE = 20
const BOX_RADIUS = 4
const ICON_SIZE = 10

// Invisible hit-area extension: the box's hover (and, for linked policies,
// click) target reaches this many px past its painted edges, so sweeping
// across a policies row never crosses dead space between squares — the
// in-canvas hover detail would flash off in the 2px gaps otherwise.
const HOVER_HIT_EXTENSION_PX = 4

const hitExtension = (
  <span
    aria-hidden
    className="absolute"
    style={{ inset: -HOVER_HIT_EXTENSION_PX }}
  />
)

// Flat-top hexagon outline in a 0..100 viewBox, inset a few units so a
// non-scaling 1px stroke stays inside the box bounds.
const HEXAGON_POINTS = "25,3 75,3 97,50 75,97 25,97 3,50"

const layoutTransition = { duration: 0.1, ease: "linear" as const }

type PolicyItemProps = {
  policy: Policy
  fillColor: string
  borderColor: string
  borderStyle: "solid" | "dashed"
  iconColor: string
  borderRadius?: number
  strokeWidth?: number
  squareSize?: number
  // Narrows the box WIDTH only, leaving its height at squareSize/BOX_SIZE — so
  // the icons read smaller without losing height. Falls back to the square size.
  boxWidth?: number
  // Box outline shape. "rounded" (default) is the CSS border-radius box; "hexagon"
  // swaps the border for an SVG hexagon (fillColor fill + borderColor stroke), so
  // it can read as a filled OR outlined hexagon.
  shape?: "rounded" | "hexagon"
  // Static CSS fill applied directly to the box (not animated), e.g. a dither
  // dot-screen via { backgroundImage, backgroundSize }. When set, the box's
  // animated solid `background` is dropped so the pattern shows through.
  fillPatternStyle?: CSSProperties
  // Drop the status glyph so the box reads as a plain (tiny) square. See HUD 78,
  // whose policy boxes shrink to the labor-square size, where the icon won't fit.
  hideIcon?: boolean
  onHoverPolicy?: (policyId: string | null) => void
}

const PolicyItem = ({
  policy, fillColor, borderColor, borderStyle, iconColor, borderRadius, strokeWidth, squareSize, boxWidth, shape = "rounded", fillPatternStyle, hideIcon, onHoverPolicy,
}: PolicyItemProps) => {
  const IconComponent = policy.icon ? POLICY_ICONS[policy.icon] : undefined
  const reference = policy.reference
  const isHexagon = shape === "hexagon"
  const width = boxWidth ?? squareSize ?? BOX_SIZE
  const height = squareSize ?? BOX_SIZE

  // Tabs 23–27: while this square is THE hovered policy (its detail showing
  // in the below-HUD map), it marks itself with the variant's treatment —
  // see the POLICY_SQUARE_* consts in policyAppearance.ts. "Filled" squares
  // (the red in-discussion boxes) get each treatment's inverse where the
  // straight red-on-red would vanish. Rounded boxes only; hexagons keep the
  // stock look.
  const hoverSwap = usePolicyHoverSwap()
  const layoutAnimationDisabled = usePolicyLayoutAnimationDisabled()
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  const squareHighlight = hoverSwap?.squareHighlight
  const hoveredPolicyId = hoverSwap?.hoveredPolicyId
  const highlightLit =
    !isHexagon && squareHighlight !== undefined && hoveredPolicyId === policy.id
  const isFilledSquare = fillColor !== "transparent"
  const invert =
    highlightLit && squareHighlight === "red-fill-invert"
      ? isFilledSquare
        ? POLICY_SQUARE_RED_FILL_INVERT_STYLE.filled
        : POLICY_SQUARE_RED_FILL_INVERT_STYLE.outlined
      : undefined
  const insetRing = highlightLit && squareHighlight === "inset-ring"
    ? POLICY_SQUARE_INSET_RING_STYLE
    : undefined
  const halo = highlightLit && squareHighlight === "halo"
    ? POLICY_SQUARE_HALO_STYLE
    : undefined
  const effectiveFillColor = invert?.fillColor ?? fillColor
  const effectiveBorderColor =
    invert?.borderColor ??
    (insetRing
      ? isFilledSquare
        ? insetRing.filledRingColor
        : insetRing.outlinedRingColor
      : borderColor)
  const effectiveIconColor = invert?.iconColor ?? iconColor

  // Hexagon: the motion box carries no border/fill — an SVG polygon draws the
  // shape behind the icon. Use explicit longhand resets (not the `border`
  // shorthand) so motion-react fully clears the box: otherwise Tailwind's default
  // gray border-color shows as a gray box around the hexagon. Rounded: the usual
  // CSS border-radius box.
  const boxStyle = isHexagon
    ? {
        width,
        height,
        position: "relative" as const,
        backgroundColor: "transparent",
        borderWidth: 0,
        borderStyle: "none" as const,
        outline: "none",
        boxShadow: "none",
      }
    : {
        width,
        height,
        // Anchors the absolute hit-extension span to this box.
        position: "relative" as const,
        borderRadius: borderRadius ?? BOX_RADIUS,
        // inset-ring: the border thickens INWARD (border-box), so the grid
        // never moves.
        borderWidth: insetRing ? insetRing.ringWidthPx : strokeWidth ?? 1,
        borderStyle,
        // halo: a page-background sliver then the red ring, painted as
        // box-shadows — zero layout, may brush the neighbouring squares.
        // The keys stay PRESENT while the treatment is merely off (motion
        // doesn't clear an inline style whose key disappears between
        // renders — the ring would stick to every square once hovered);
        // only their values toggle.
        ...(squareHighlight === "halo" && {
          boxShadow: halo
            ? `0 0 0 ${halo.gapWidthPx}px ${halo.gapColor}, 0 0 0 ${halo.gapWidthPx + halo.ringWidthPx}px ${halo.ringColor}`
            : "0 0 0 0 rgba(0,0,0,0)",
          zIndex: halo ? 1 : "auto",
        }),
        // pop overlaps its neighbours while scaled — keep it on top even when
        // the pointer has moved on to the map below (sticky hover). Same
        // always-present-key rule as the halo above.
        ...(squareHighlight === "pop" && {
          zIndex: highlightLit ? 1 : "auto",
        }),
        ...fillPatternStyle,
      }
  const iconElement = !hideIcon && IconComponent ? (
    <IconComponent size={ICON_SIZE} strokeWidth={2.2} />
  ) : null

  const sharedMotion = {
    // No shared-layout tween while the host surface is itself moving (the
    // mobile sheet's rise) — the boxes would chase the pane and swim in
    // after the rest of the panel. See policyLayoutAnimationContext.
    layoutId: layoutAnimationDisabled ? undefined : policy.id,
    animate: isHexagon
      ? { color: iconColor }
      : {
          ...(fillPatternStyle ? {} : { background: effectiveFillColor }),
          borderColor: effectiveBorderColor,
          borderStyle,
          color: effectiveIconColor,
          // pop: the hovered square rises over its neighbours by transform
          // alone — no reflow.
          scale:
            highlightLit && squareHighlight === "pop"
              ? POLICY_SQUARE_POP_STYLE.scale
              : 1,
        },
    // With a square-highlight treatment active, the stock hover dim (an
    // opacity de-emphasis) gives way to the treatment. No hover effects at
    // all on hover-less surfaces (framer's whileHover fires on touch taps).
    whileHover: hoverSwapsDisabled
      ? undefined
      : squareHighlight ? { zIndex: 1 } : { opacity: .5, zIndex: 1 },
    transition: {
      layout: layoutTransition,
      default: { duration: 0.1, ease: "linear" as const },
    },
    style: boxStyle,
    className: "flex items-center justify-center",
    onMouseEnter: () => onHoverPolicy?.(policy.id),
    onMouseLeave: () => onHoverPolicy?.(null),
    // Lets overlays anchor to a specific square (e.g. the unified POLICY
    // card renders right above the hovered box — see PoliciesPanel).
    "data-policy-id": policy.id,
    // Feeds the /analytics "Hovered elements" panel via ui/hoverTracking.tsx.
    "data-track-hover": `hud-policy:${policy.id}`,
  }

  const content = isHexagon ? (
    <>
      {hitExtension}
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
      >
        <polygon
          points={HEXAGON_POINTS}
          fill={fillColor}
          stroke={borderColor}
          strokeWidth={strokeWidth ?? 1}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {iconElement && (
        <span className="relative flex">{iconElement}</span>
      )}
    </>
  ) : (
    <>
      {hitExtension}
      {iconElement}
      {/* red-bar: a short red rule just under the hovered square (absolute —
          no layout). */}
      {highlightLit && squareHighlight === "red-bar" && (
        <span
          aria-hidden
          className="absolute"
          style={{
            left: 0,
            right: 0,
            bottom: -(
              POLICY_SQUARE_RED_BAR_STYLE.barGapPx +
              POLICY_SQUARE_RED_BAR_STYLE.barHeightPx
            ),
            height: POLICY_SQUARE_RED_BAR_STYLE.barHeightPx,
            borderRadius: POLICY_SQUARE_RED_BAR_STYLE.barRadiusPx,
            background: POLICY_SQUARE_RED_BAR_STYLE.barColor,
          }}
        />
      )}
    </>
  )

  if (reference) {
    return (
      <motion.a
        {...sharedMotion}
        href={reference.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`${policy.label} — open source in new tab`}
      >
        {content}
      </motion.a>
    )
  }

  return (
    <motion.div {...sharedMotion}>
      {content}
    </motion.div>
  )
}

export default PolicyItem
